import { useState, useEffect, lazy, Suspense } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Save, X, Coins, Trash2, Clock, User } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { format, addDays, startOfWeek } from 'date-fns';
import { he } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useUpdateWeeklyTaskTemplate, useDeleteWeeklyTaskTemplate, WeeklyTask } from '@/hooks/useWeeklyTasks';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUserIcons } from '@/hooks/useUserIcons';

// Convert PascalCase to kebab-case for dynamic import
const toKebabCase = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
};

// Dynamic icon component
function DynamicIcon({ iconName, className, style }: { iconName: string; className?: string; style?: React.CSSProperties }) {
  const kebabName = toKebabCase(iconName);
  const iconImport = dynamicIconImports[kebabName as keyof typeof dynamicIconImports];
  
  if (!iconImport) {
    return <User className={className} style={style} />;
  }
  
  const LucideIcon = lazy(iconImport);
  
  return (
    <Suspense fallback={<User className={className} style={style} />}>
      <LucideIcon className={className} style={style} />
    </Suspense>
  );
}

const USERS = [
  { id: 'matan', name: 'מתן', color: 'hsl(var(--skylight-blue))' },
  { id: 'heila', name: 'הילה', color: 'hsl(var(--skylight-pink))' },
  { id: 'asaf', name: 'אסף', color: 'hsl(var(--skylight-green))' }
];

const EN_DAYS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const DAY_TO_INDEX: Record<string, number> = {
  'Sunday': 0,
  'Monday': 1,
  'Tuesday': 2,
  'Wednesday': 3,
  'Thursday': 4,
  'Friday': 5,
  'Saturday': 6,
};

interface EditEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: WeeklyTask | null;
  weekOffset: number;
  isAdmin?: boolean;
}

export function EditEventDialog({ open, onOpenChange, task, weekOffset, isAdmin = false }: EditEventDialogProps) {
  const [selectedUser, setSelectedUser] = useState<string>('matan');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [coins, setCoins] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedHour, setSelectedHour] = useState<number>(9);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const { getUserIcon } = useUserIcons();
  const updateTemplate = useUpdateWeeklyTaskTemplate();
  const deleteTemplate = useDeleteWeeklyTaskTemplate();

  // Initialize form with task data when dialog opens
  useEffect(() => {
    if (task && open) {
      setSelectedUser(task.user_name);
      setTitle(task.title);
      setNotes(task.description || '');
      setCoins(task.coins || 0);
      setSelectedHour(task.scheduled_hour ?? 9);
      setSelectedMinute(task.scheduled_minute ?? 0);
      
      // Calculate the date based on scheduled_day and weekOffset
      if (task.scheduled_day) {
        const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
        const adjustedWeekStart = addDays(weekStart, weekOffset * 7);
        const dayIndex = DAY_TO_INDEX[task.scheduled_day] ?? 0;
        const taskDate = addDays(adjustedWeekStart, dayIndex);
        setSelectedDate(taskDate);
      } else {
        setSelectedDate(new Date());
      }
    }
  }, [task, open, weekOffset]);

  const handleSave = async () => {
    if (!task) return;
    
    if (!title.trim()) {
      toast.error('נא להזין כותרת לאירוע');
      return;
    }

    if (!selectedDate) {
      toast.error('נא לבחור תאריך');
      return;
    }

    setIsLoading(true);

    try {
      const dayOfWeek = selectedDate.getDay();
      const scheduledDay = EN_DAYS[dayOfWeek];

      await updateTemplate.mutateAsync({
        id: task.id,
        updates: {
          title: title,
          hebrew_title: title,
          description: notes || null,
          hebrew_description: notes || null,
          coins: selectedUser === 'matan' ? coins : 0,
          user_name: selectedUser,
        },
        schedules: [{
          scheduled_day: scheduledDay,
          scheduled_hour: selectedHour,
          scheduled_minute: selectedMinute,
        }],
      });

      toast.success('האירוע עודכן בהצלחה');
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('שגיאה בעדכון האירוע');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    
    if (!confirm('האם אתה בטוח שברצונך למחוק את האירוע?')) return;

    setIsLoading(true);

    try {
      await deleteTemplate.mutateAsync(task.id);
      toast.success('האירוע נמחק בהצלחה');
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('שגיאה במחיקת האירוע');
    } finally {
      setIsLoading(false);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i); // 0:00 - 23:00 (24H format)
  const minutes = Array.from({ length: 60 }, (_, i) => i); // 0-59

  const getFormattedDate = () => {
    if (!selectedDate) return 'בחר תאריך';
    return format(selectedDate, "EEEE, d בMMMM", { locale: he });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 overflow-hidden bg-slate-800 border-slate-700 text-slate-100" dir="rtl">
        <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
          <DialogTitle className="text-right text-slate-100">{isAdmin ? 'עריכת אירוע' : 'צפייה באירוע'}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6 overflow-y-auto">
          <div className="space-y-4 py-4">
            {/* User Selection - Buttons with icons */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-right block">שיוך למשתמש</Label>
              <div className="flex gap-2 p-1">
                {USERS.map(user => {
                  const userIconName = getUserIcon(user.id);
                  const isSelected = selectedUser === user.id;
                  return (
                    <Button
                      key={user.id}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      className={cn(
                        "flex-1 gap-2 transition-all",
                        isSelected && "ring-2 ring-offset-2 ring-offset-slate-800",
                        !isSelected && "bg-slate-700 border-slate-600 hover:bg-slate-600",
                        !isAdmin && "pointer-events-none"
                      )}
                      style={{
                        backgroundColor: isSelected ? user.color : undefined,
                        borderColor: isSelected ? user.color : undefined,
                        ...(isSelected ? {} : { color: user.color }),
                        ...(isSelected ? { '--tw-ring-color': user.color } as React.CSSProperties : {})
                      }}
                      onClick={() => isAdmin && setSelectedUser(user.id)}
                      disabled={!isAdmin}
                    >
                      {userIconName ? (
                        <DynamicIcon iconName={userIconName} className="h-4 w-4" />
                      ) : (
                        <User className="h-4 w-4" />
                      )}
                      {user.name}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-right block">כותרת האירוע</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="הזן כותרת..."
                className="text-right bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                readOnly={!isAdmin}
              />
            </div>

            {/* Date Picker */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-right block">תאריך</Label>
              <Popover>
                <PopoverTrigger asChild disabled={!isAdmin}>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-right font-normal bg-slate-700 border-slate-600 text-slate-100 hover:bg-slate-600",
                      !selectedDate && "text-slate-400",
                      !isAdmin && "pointer-events-none"
                    )}
                    disabled={!isAdmin}
                  >
                    <CalendarIcon className="ml-2 h-4 w-4" />
                    {getFormattedDate()}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="pointer-events-auto bg-slate-800 text-slate-100"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Picker - Separate */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 flex-row-reverse justify-start text-slate-300">
                <Clock className="h-4 w-4" />
                שעה
              </Label>
              <div className="flex items-center justify-center gap-2 p-3 bg-slate-700/50 rounded-lg">
                {/* Hours */}
                <div className="flex flex-col items-center">
                  <Select value={selectedHour.toString()} onValueChange={(v) => setSelectedHour(parseInt(v))} disabled={!isAdmin}>
                    <SelectTrigger className="w-20 h-14 text-2xl font-bold text-center bg-slate-700 border-slate-600 text-slate-100 border-2">
                      <SelectValue className="text-slate-100" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 shadow-lg z-[100] max-h-[200px]">
                      {hours.map(hour => (
                        <SelectItem key={hour} value={hour.toString()} className="text-slate-100 hover:bg-slate-700 focus:bg-slate-700">
                          {hour.toString().padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-400 mt-1">שעה</span>
                </div>
                
                <span className="text-3xl font-bold text-slate-100">:</span>
                
                {/* Minutes */}
                <div className="flex flex-col items-center">
                  <Select value={selectedMinute.toString()} onValueChange={(v) => setSelectedMinute(parseInt(v))} disabled={!isAdmin}>
                    <SelectTrigger className="w-20 h-14 text-2xl font-bold text-center bg-slate-700 border-slate-600 text-slate-100 border-2">
                      <SelectValue className="text-slate-100" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 shadow-lg z-[100] max-h-[200px]">
                      {minutes.map(minute => (
                        <SelectItem key={minute} value={minute.toString()} className="text-slate-100 hover:bg-slate-700 focus:bg-slate-700">
                          {minute.toString().padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-400 mt-1">דקות</span>
                </div>
              </div>
            </div>

            {/* Coins (only for Matan) */}
            {selectedUser === 'matan' && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 flex-row-reverse justify-start text-slate-300">
                  <Coins className="h-4 w-4 text-yellow-500" />
                  מטבעות
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={coins}
                  onChange={(e) => setCoins(parseInt(e.target.value) || 0)}
                  className="text-right bg-slate-700 border-slate-600 text-slate-100"
                  readOnly={!isAdmin}
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-right block">הערות</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="הוסף הערות..."
                className="text-right min-h-[80px] bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                readOnly={!isAdmin}
              />
            </div>
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="flex justify-between gap-2 p-6 pt-2 border-t border-slate-700 flex-shrink-0 bg-slate-800">
          {isAdmin ? (
            <>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isLoading}
              >
                <Trash2 className="h-4 w-4 ml-2" />
                מחק
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                  className="bg-slate-700 border-slate-600 text-slate-100 hover:bg-slate-600"
                >
                  <X className="h-4 w-4 ml-2" />
                  ביטול
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={isLoading}
                  className="bg-skylight-blue hover:bg-skylight-blue/90"
                >
                  <Save className="h-4 w-4 ml-2" />
                  שמור
                </Button>
              </div>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full bg-slate-700 border-slate-600 text-slate-100 hover:bg-slate-600"
            >
              <X className="h-4 w-4 ml-2" />
              סגור
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
